import { NoteAudioPlayer } from '../../core/NoteAudioPlayer.js';

/**
 * PlatformerNotes — runtime controller for Music Note elements (and
 * pass-through "sound" area triggers).
 *
 * Why a controller (vs. just letting each NoteEntity own a sound)?
 * Several notes referencing the same MP3 form a SYNCED SEQUENCE — each
 * plays a consecutive ~1-second slice in left-to-right order. That
 * ordering can only be assigned globally (you need to see every note
 * in the group at once), so a single owner is the right shape. It also
 * gives us one place to:
 *
 *   - own the NoteAudioPlayer + master gain;
 *   - preload every distinct audio src up-front so the first contact
 *     never stutters waiting for fetch/decode;
 *   - run edge-triggered playback (one play per ENTER, optional retrigger
 *     on re-entry) without polluting the entity class with state the
 *     entity itself shouldn't care about.
 *
 * Scheduled-queue playback (Mario-Wonder "tune at slice tempo"):
 *   - For a MULTI-NOTE GROUP (2+ notes sharing the same audio
 *     clip), each note's slice is scheduled into a per-group queue
 *     on the audio clock: the new slice starts exactly when the
 *     previous one ends. That way the tune always plays at its
 *     natural tempo (Slice Duration milliseconds per note) no
 *     matter how fast the player runs through — touch every note
 *     in a single frame and the song still plays out in full over
 *     `notesCount * Slice Duration` seconds. Slice Duration directly
 *     controls the tempo: 1000 ms = the song plays at its native
 *     pace, 500 ms = the song plays at 2× speed, 2000 ms = half
 *     speed. Slow walks the player just past slice tempo produce
 *     gaps of silence between slices (the queue empties before the
 *     next note triggers) — that's the tune naturally tracking the
 *     player when they stop.
 *   - The LAST note in a multi-note group (highest auto-assigned
 *     slice index) is scheduled with its normal slice PLUS `tailMs`
 *     of extra audio read from the same clip, faded out smoothly
 *     over the tail. So the tune always ends on a held, decaying
 *     chord rather than stopping hard.
 *   - For a SOLO note (group of one), the slice plays
 *     fire-and-forget — same per-slice SFX behaviour as before, no
 *     queue bookkeeping.
 *   - 'once'-retrigger notes (coin-like) consume themselves on the
 *     first contact: slice plays fire-and-forget, the note
 *     disappears, no group bookkeeping.
 *
 * `tailMs` controls the EXTENSION + fade length on the last note's
 * slice. `-1` = auto (5000 ms on the last note in a multi-group,
 * 0 elsewhere). `0` = no tail (just the slice). Positive ms =
 * explicit extension. The fade is scheduled on the source's
 * GainNode before `start()`, so the audio thread owns the envelope
 * and frame drops can't make it stutter.
 *
 * Public API mirrors the lifecycle of the scene:
 *   - new PlatformerNotes(theme)
 *   - controller.mount(stage, notes)          — after entities spawn
 *   - controller.update(player)               — every frame
 *   - controller.handleSoundZone(zoneEntity)  — for area effect=sound
 *   - controller.handleMusicZone(zoneEntity)  — for area effect=music
 *   - controller.reset()                      — on respawn / restart
 *   - controller.destroy()                    — on scene teardown
 */

/** Default tail (ms) added to the LAST note's slice in a multi-note group. */
const AUTO_TAIL_MS_LAST = 5000;
/** Quick fade applied when a respawn / teardown cuts any in-flight playback. */
const RESET_FADE_MS = 60;
/** A queue that's been silent for this many seconds gets reset to "now"
 *  so a fresh run-through doesn't queue against a stale clock. */
const QUEUE_GAP_RESET_SEC = 0.05;

export class PlatformerNotes {
    /** @type {NoteAudioPlayer|null} */
    _audio = null;
    /** All NoteEntity instances handed to mount(). */
    _notes = [];
    /** Per-note edge-detect state — id → wasInside boolean. */
    _wasInside = new Map();
    /** Per-note "consumed" flag for retrigger=once. */
    _consumed = new Set();
    /** Map<src, totalDuration>. Filled by preload(). Optional. */
    _durations = new Map();
    /** All live + scheduled slices per audio group — groupKey →
     *  Set<{handle, note}>. A Set (not a single handle) because the
     *  scheduled-queue model can have several slices in flight at
     *  once (one playing, several queued for the future). Used by
     *  silence / reset / destroy to fade them all. */
    _groupActive = new Map();
    /** AudioContext time (sec) at which each group's queue is
     *  scheduled to end — i.e. when the next slice should start.
     *  A new trigger uses max(audio.now(), queueEnd) so fast
     *  triggers queue back-to-back and slow triggers leave gaps. */
    _groupQueueEnd = new Map();
    /** Notes-per-group count — distinguishes solo (no queue) from
     *  multi (scheduled queue playback). */
    _groupSize = new Map();
    /** Highest resolvedSliceIndex per group — defines "the last note"
     *  (the one that gets the extended tail with fade). */
    _lastSliceByGroup = new Map();
    /** Currently-playing background music track started by an Audio
     *  Zone. `{ handle, src }` or null. The zones address a single
     *  named slot ("the music") so a new Play action can crossfade
     *  the previous track into the new one. */
    _currentMusic = null;
    _theme = null;

    /**
     * @param {object} theme  SkinResolver (resolves audio slot keys → srcs)
     * @param {{ audio?: NoteAudioPlayer }} [opts]
     *        `audio` — adopt a pre-warmed player (the stage preloader
     *        decodes clips into one BEFORE mount so first contact never
     *        stutters). The controller takes ownership either way and
     *        destroys it on teardown.
     */
    constructor(theme, opts = {}) {
        this._theme = theme;
        this._audio = (opts.audio instanceof NoteAudioPlayer)
            ? opts.audio
            : new NoteAudioPlayer({ volume: 1 });
    }

    /**
     * Resolve a Music Note's `audio` prop (a "game.slot" key like
     * `platformer.audio.1`) into the actual src the audio player can
     * fetch. Returns null when the slot has no clip imported yet.
     */
    _resolveAudioSrc(audioKey) {
        if (!audioKey || !this._theme) return null;
        return this._theme.get?.(audioKey) || null;
    }

    /**
     * After the scene spawns entities, hand the note list in. Notes
     * are grouped by their `audio` slot key; within each group they're
     * sorted left-to-right (then top-to-bottom) and assigned auto
     * slice indices 0..n-1. A note with a numeric `sliceIndex` prop
     * keeps that override, the rest fill in around it.
     *
     * Also kicks off a best-effort preload of every distinct clip so
     * the first contact lands without seek latency.
     */
    mount(stage, notes) {
        this._notes = Array.isArray(notes) ? notes.slice() : [];
        this._wasInside.clear();
        this._consumed.clear();
        this._stopCurrentMusic(0);
        this._groupActive.clear();
        this._groupQueueEnd.clear();
        this._groupSize.clear();
        this._lastSliceByGroup.clear();

        // Group by audio slot key — slot-less notes (no clip set) live
        // in their own group keyed by '' and never auto-index.
        const groups = new Map();
        for (const n of this._notes) {
            const key = String(n.props?.audio || '');
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(n);
        }

        const autoSrcs = [];
        for (const [audioKey, group] of groups.entries()) {
            // Manual-override pass: notes with a finite numeric sliceIndex
            // lock their slot. Everyone else is auto-assigned the next
            // free index in left-to-right order, skipping locked slots.
            const sorted = group.slice().sort(byPosition);
            const taken = new Set();
            for (const n of sorted) {
                const v = Number(n.props?.sliceIndex);
                if (Number.isFinite(v) && v >= 0) {
                    n.resolvedSliceIndex = Math.floor(v);
                    taken.add(n.resolvedSliceIndex);
                }
            }
            let cursor = 0;
            for (const n of sorted) {
                const v = Number(n.props?.sliceIndex);
                if (Number.isFinite(v) && v >= 0) continue;
                while (taken.has(cursor)) cursor++;
                n.resolvedSliceIndex = cursor;
                taken.add(cursor);
                cursor++;
            }

            this._groupSize.set(audioKey, sorted.length);

            // Track the highest resolved slice index so the fire path
            // can tag exactly ONE note in the group as the "finale"
            // that gets the extended tail with fade.
            let maxIdx = -1;
            for (const n of sorted) {
                const i = n.resolvedSliceIndex | 0;
                if (i > maxIdx) maxIdx = i;
            }
            this._lastSliceByGroup.set(audioKey, maxIdx);

            if (audioKey) {
                const src = this._resolveAudioSrc(audioKey);
                if (src) autoSrcs.push(src);
            }
        }

        // Preload (best-effort) so the first contact doesn't stutter.
        if (autoSrcs.length) {
            const uniq = Array.from(new Set(autoSrcs));
            this._audio?.preload(uniq).then(() => this._recordDurations(uniq))
                .catch(() => {});
        }
    }

    /**
     * After preload, the NoteAudioPlayer has each clip's AudioBuffer
     * cached — read its `duration` so the per-note slice clamp can be
     * accurate (a final note in a sequence shouldn't try to play past
     * the end of the buffer). Best-effort; missing values just keep
     * the entity's authored `sliceDuration` as-is.
     */
    async _recordDurations(srcs) {
        if (!this._audio) return;
        for (const src of srcs) {
            const buf = await this._audio.loadBuffer(src).catch(() => null);
            if (buf && Number.isFinite(buf.duration)) this._durations.set(src, buf.duration);
        }
    }

    /**
     * Per-frame tick — checks every note for an enter edge against the
     * player AABB. Fires `_fireNote` on the rising edge (enter); each
     * slice is then left alone to play out for its full Slice Duration
     * (+ tail for the last note in the group) so the tune chains
     * continuously as the player runs. `retrigger === 'once'` consumes
     * the note on the first play and hides it like a coin.
     */
    update(player) {
        if (!player || !player.alive) return;
        const px = player.x, py = player.y, pw = player.w, ph = player.h;
        for (const n of this._notes) {
            if (!n.alive) continue;
            const id = n.id || n;       // entity id from stage record, else identity
            const inside = aabbOverlap(px, py, pw, ph, n.x, n.y, n.w, n.h);
            const was = this._wasInside.get(id) || false;
            if (inside && !was) {
                if (!this._consumed.has(id)) {
                    this._fireNote(n);
                    if ((n.props?.retrigger || 'always') === 'once') {
                        this._consumed.add(id);
                        n.alive = false;
                        if (n.display) n.display.visible = false;
                    }
                }
            }
            this._wasInside.set(id, inside);
        }
    }

    /**
     * Play this note's sound. Branches:
     *   - oneShot:    play the whole clip from the start (overlap is
     *                 allowed so several taps can layer like a chord).
     *   - solo slice: single-note group — play just this note's slice
     *                 fire-and-forget.
     *   - multi slice: 2+ notes share this audio. Each note's slice
     *                 is SCHEDULED into a per-group queue on the
     *                 audio clock: a new trigger starts when the
     *                 previous slice ends, so the tune always plays
     *                 at slice tempo regardless of how fast the
     *                 player runs. The LAST note (highest auto-
     *                 assigned slice index) is scheduled with its
     *                 slice + extended tail (faded out) so the tune
     *                 always ends on a held, decaying chord.
     */
    _fireNote(note) {
        const audioKey = note.props?.audio;
        const src = this._resolveAudioSrc(audioKey);
        if (!src) {
            // Visual feedback even with no audio bound so the designer
            // can still see the trigger fired during play-test.
            note.pulse?.();
            return;
        }
        const mode = note.props?.mode === 'oneShot' ? 'oneShot' : 'slice';
        const vol = Number.isFinite(note.props?.volume) ? clamp01(note.props.volume) : 1;

        if (mode === 'oneShot') {
            this._audio?.playOneShot(src, vol);
            note.pulse?.();
            return;
        }

        const { offset, clipped } = this._sliceFor(note, src);
        if (clipped <= 0.02) {
            note.pulse?.();
            return;
        }

        const groupKey = String(audioKey || '');
        const groupSize = this._groupSize.get(groupKey) || 0;

        if (groupSize < 2) {
            // SOLO note: SFX-style chunk, fire-and-forget. No tail —
            // a single note isn't a sequence that needs a finale.
            this._audio?.playSlice(src, offset, clipped, vol);
            note.pulse?.();
            return;
        }

        // MULTI-NOTE GROUP — schedule onto the per-group queue. New
        // slices start exactly when the previous one ends (in audio-
        // clock time), so a burst of fast triggers still produces the
        // tune at its natural slice tempo instead of overlapping a
        // muddy chord of slices at different song positions.
        const tailMs = this._resolveTailMs(note);
        const tailSec = Math.max(0, tailMs / 1000);
        let playDur = clipped;
        let fadeSec = 0;
        if (tailSec > 0) {
            // Extend the LAST note's slice by the requested tail
            // (capped by what's actually left in the clip past the
            // slice), with a smooth linear fade to silence so the
            // tune ends on a held, decaying chord.
            const total = this._durations.get(src);
            const maxExtra = Number.isFinite(total)
                ? Math.max(0, total - offset - clipped)
                : tailSec;
            const extra = Math.min(tailSec, maxExtra);
            playDur = clipped + extra;
            fadeSec = extra > 0 ? extra : Math.min(tailSec, playDur);
        }

        // Compute the scheduled start. If the queue has been silent
        // for more than a tiny gap, reset it to "now" so a fresh
        // run-through doesn't queue against a stale clock. The slice
        // ends at start + clipped (the TAIL is bonus audio that
        // overflows past the queue's "this slice's slot" — we don't
        // push the queue forward by the tail length, so the next
        // note after a tail-bearing one still lands at slice tempo).
        const audioNow = this._audio?.now?.() || 0;
        const prevEnd = this._groupQueueEnd.get(groupKey) || 0;
        const start = (prevEnd > audioNow + QUEUE_GAP_RESET_SEC)
            ? prevEnd
            : audioNow;
        const handle = this._audio?.playSlice(src, offset, playDur, vol, fadeSec, start);
        if (handle) {
            this._groupQueueEnd.set(groupKey, start + clipped);
            this._trackGroupHandle(groupKey, handle, note);
        }
        note.pulse?.();
    }

    /**
     * Add `handle` to the live set for `groupKey` and arrange for
     * automatic removal when the slice finishes (naturally or via
     * stopHandle). Bounded set size = "however many slices are
     * still scheduled or playing at once" — typically a small
     * number even when the player runs through a long row of
     * notes, because each slice finishes within Slice Duration.
     */
    _trackGroupHandle(groupKey, handle, note) {
        let set = this._groupActive.get(groupKey);
        if (!set) { set = new Set(); this._groupActive.set(groupKey, set); }
        const entry = { handle, note };
        set.add(entry);
        // Best-effort GC: poll on a coarse timer rather than
        // hooking source.onended (which the audio engine already
        // owns). Cheap because the timer only fires until the
        // slice ends; once removed we stop polling.
        const tick = () => {
            if (handle.ended || handle.stopped) { set.delete(entry); return; }
            setTimeout(tick, 250);
        };
        setTimeout(tick, 250);
    }

    /**
     * Resolve a note's slice window: `(offset, duration)` clamped to
     * the actual clip length (so a slice past the end shrinks instead
     * of being rejected by Web Audio). Caller decides what to do with
     * a zero-or-tiny `clipped`. Solo-note path only — the multi path
     * computes its own offset / end-of-file duration inline.
     */
    _sliceFor(note, src) {
        const dur = Math.max(0.05, _sliceSeconds(note));
        const idx = Math.max(0, note.resolvedSliceIndex | 0);
        const offset = idx * dur;
        const total = this._durations.get(src);
        const clipped = Number.isFinite(total)
            ? Math.max(0, Math.min(dur, total - offset))
            : dur;
        return { offset, clipped };
    }

    /**
     * Choose the tail (ms) for this note's playback. New schema
     * exposes a two-control UI:
     *
     *   - `tailMode`:
     *       'auto' → 5000 ms tail with fade on the LAST note of a
     *               multi-note group, 0 elsewhere (default).
     *       'fade' → use `tailMs` literally, but only on the LAST
     *               note of a multi-note group; interior notes
     *               still play just their slice.
     *       'cut'  → no tail anywhere (slice ends, music stops dead).
     *
     * For backward compatibility, if `tailMode` isn't set we fall
     * back to the legacy single-field `tailMs` semantics:
     *
     *   - `tailMs >= 0` → use it verbatim (any note).
     *   - otherwise   → same auto rule as `tailMode = 'auto'`.
     */
    _resolveTailMs(note) {
        const mode = note.props?.tailMode;
        const isLast = this._isLastNoteInGroup(note);

        if (mode === 'cut') return 0;
        if (mode === 'fade') {
            if (!isLast) return 0;
            const v = Number(note.props?.tailMs);
            return Number.isFinite(v) && v >= 0 ? v : 1000;
        }
        if (mode === 'auto') {
            return isLast ? AUTO_TAIL_MS_LAST : 0;
        }

        // Legacy stages: no tailMode in props → use the old single-
        // field rule so previously authored levels keep their
        // exact fade behaviour.
        const v = Number(note.props?.tailMs);
        if (Number.isFinite(v) && v >= 0) return v;
        return isLast ? AUTO_TAIL_MS_LAST : 0;
    }

    /**
     * True iff `note` is the highest-slice-index note in its
     * multi-note group (the one that gets the "finale" tail).
     * False for solo notes and interior notes of any group.
     */
    _isLastNoteInGroup(note) {
        const audioKey = String(note.props?.audio || '');
        const groupSize = this._groupSize.get(audioKey) || 0;
        if (groupSize < 2) return false;
        const lastIdx = this._lastSliceByGroup.get(audioKey);
        if (!Number.isFinite(lastIdx)) return false;
        return (note.resolvedSliceIndex | 0) === lastIdx;
    }

    /**
     * Forwarded from the scene when an area.trigger with effect='sound'
     * fires its 'sound' verdict. The area entity itself handles the
     * once-per-entry edge detection (`_inside` flag) and the scene calls
     * `_refreshAreaInsideState` to clear the flag on exit.
     */
    handleSoundZone(zone) {
        if (!zone) return;
        const src = this._resolveAudioSrc(zone.props?.audio);
        if (!src) return;
        const vol = Number.isFinite(zone.props?.volume) ? clamp01(zone.props.volume) : 1;
        this._audio?.playOneShot(src, vol);
    }

    /**
     * Forwarded from the scene when an area.trigger with
     * effect='music' fires its 'music' verdict. Branches on
     * `musicAction`:
     *
     *   - 'stop':  fade out EVERY currently-playing audio source
     *              over `musicFadeMs` — the background music track
     *              AND every still-ringing note-group slice (the
     *              "last note" tail counts). A designer can rely on
     *              a Stop zone leaving the level genuinely silent
     *              regardless of which kind of source last played.
     *   - 'play':  start the chosen audio as the new music track. If
     *              another track is already playing it gets faded
     *              out over `musicFadeMs` while the new one fades
     *              IN over the same duration — i.e. a crossfade.
     *              Any still-ringing note tails are also faded out
     *              with the old track so they don't bleed into the
     *              new music. A fade of 0 hard-cuts. Choosing the
     *              same audio that's already playing is a no-op so
     *              a designer can drop redundant Play zones along
     *              a corridor without restarting the track.
     *
     * The zone is edge-triggered (re-armed by the scene on exit), so
     * the player has to leave and re-enter to fire it again.
     */
    handleMusicZone(zone) {
        if (!zone) return;
        const props = zone.props || {};
        const action = props.musicAction === 'stop' ? 'stop' : 'play';
        const fadeMs = Math.max(0, Number(props.musicFadeMs) || 0);

        if (action === 'stop') {
            this._silenceAllMusic(fadeMs);
            return;
        }

        const src = this._resolveAudioSrc(props.audio);
        if (!src) return;

        // No-op if the requested track is already the current music.
        // Lets the designer scatter "ensure this is playing" zones
        // along a path without restarting the loop every entry.
        const cur = this._currentMusic;
        if (cur && cur.src === src && cur.handle && !cur.handle.ended && !cur.handle.stopped) {
            return;
        }

        const vol = Number.isFinite(props.volume) ? clamp01(props.volume) : 1;
        const loop = props.loop === 'yes' || props.loop === true;
        const fadeSec = fadeMs / 1000;

        // Silence everything before starting the new track so any
        // note tail decays alongside the previous music instead of
        // bleeding into the new intro.
        this._silenceAllMusic(fadeMs);
        const handle = this._audio?.playMusic(src, { volume: vol, fadeInSec: fadeSec, loop });
        this._currentMusic = handle ? { handle, src } : null;
    }

    /**
     * Fade out + forget the currently-playing background music
     * track. Used internally by `_silenceAllMusic` and (with a 0 ms
     * cut) by `mount` / `destroy`.
     */
    _stopCurrentMusic(fadeMs) {
        const cur = this._currentMusic;
        if (!cur) return;
        if (cur.handle && !cur.handle.stopped) {
            try { this._audio?.stopHandle(cur.handle, Math.max(0, Number(fadeMs) || 0)); } catch (_) {}
        }
        this._currentMusic = null;
    }

    /**
     * Silence EVERY active music source: the long-running background
     * track AND every per-group note playback (live OR still queued
     * for the future, including a still-ringing "last note" tail).
     * Both layers fade over `fadeMs`, so a 0-ms cut is a hard stop
     * and a 500-ms cut is a smooth one.
     *
     * Belt-and-suspenders: we explicitly stop the tracked handles
     * we know about (background music + multi-note group entries)
     * AND ask the audio player to sweep every remaining live source
     * via `silenceAll`. That second sweep is what catches the
     * fire-and-forget sources whose handle the caller discarded —
     * solo-note slices, one-shot notes, sound-zone SFX — and is
     * the fix for the user's "Stop zone leaves ~1–2 s of tail
     * audio ringing" complaint. Without it, only sources in
     * `_groupActive` were stopped, leaving anything not in the
     * multi-note path audible until its natural decay finished.
     *
     * Called by an Audio Zone's Stop action — and as the cut-out
     * step of a Play zone's crossfade — so the music heard by the
     * player reliably matches what the designer authored regardless
     * of which kind of source happened to be ringing last.
     */
    _silenceAllMusic(fadeMs) {
        const ms = Math.max(0, Number(fadeMs) || 0);
        this._stopCurrentMusic(ms);
        for (const set of this._groupActive.values()) {
            for (const entry of set) {
                if (entry?.handle && !entry.handle.stopped) {
                    try { this._audio?.stopHandle(entry.handle, ms); } catch (_) {}
                }
            }
        }
        this._groupActive.clear();
        // Reset queue clocks so the next trigger after silence
        // starts fresh from "now" instead of catching up to a
        // stale scheduled-end timestamp.
        this._groupQueueEnd.clear();
        // Final sweep: kill every live source, including the ones
        // we don't have a tracked handle for (solo notes, one-shots,
        // sound-zone SFX). Same fade so a 10 ms Stop zone really is
        // ~10 ms to total silence.
        try { this._audio?.silenceAll(ms); } catch (_) {}
    }

    /**
     * Clear edge-detect + consumption state so a respawn behaves like
     * a fresh entry to every note. Notes that were consumed by
     * `retrigger=once` and physically hidden stay hidden — that's the
     * "you grabbed it, it's gone" semantic the original Mario coin uses.
     * Any in-flight playback is cut so a respawn doesn't leave music
     * dangling over the new spawn position.
     */
    reset() {
        this._wasInside.clear();
        // Fade out every live OR queued slice across all groups
        // so a respawn doesn't leak a still-scheduled tail over
        // the new spawn. _silenceAllMusic clears the queue clocks
        // too so the next run starts fresh.
        this._silenceAllMusic(RESET_FADE_MS);
    }

    destroy() {
        this._stopCurrentMusic(0);
        try { this._audio?.destroy?.(); } catch (_) {}
        this._audio = null;
        this._notes = [];
        this._wasInside.clear();
        this._consumed.clear();
        this._durations.clear();
        this._groupActive.clear();
        this._groupQueueEnd.clear();
        this._groupSize.clear();
        this._lastSliceByGroup.clear();
    }
}

// ── helpers ───────────────────────────────────────────────────────

function aabbOverlap(ax, ay, aw, ah, bx, by, bw, bh) {
    return ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by;
}

function byPosition(a, b) {
    // Left-to-right; ties broken by top-to-bottom so two notes at the
    // same X get a stable order without designers needing to think
    // about a tie-breaker.
    if (a.x !== b.x) return a.x - b.x;
    return a.y - b.y;
}

function clamp01(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return 1;
    return n < 0 ? 0 : n > 1 ? 1 : n;
}

/**
 * Slice length in SECONDS for a note. Reads `sliceDurationMs` (new,
 * authored in ms) first, falls back to the legacy `sliceDuration`
 * (seconds) for stages saved before the unit switch. Default 1 s.
 */
function _sliceSeconds(note) {
    const ms = Number(note?.props?.sliceDurationMs);
    if (Number.isFinite(ms) && ms > 0) return ms / 1000;
    const sec = Number(note?.props?.sliceDuration);
    if (Number.isFinite(sec) && sec > 0) return sec;
    return 1.0;
}
